import React from "react";
import { Box, Button, CssBaseline, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import Authenticate from "./pages/Authenticate";
import PublicInfo from "./pages/PublicInfo";
import Intake from "./pages/Intake";
import SellerDashboard from "./pages/SellerDashboard";
import ValuationResult from "./pages/ValuationResult";
import DealRoom from "./pages/DealRoom";
import SellerDocument from "./pages/SellerDocument";
import BuyerInformation from "./pages/BuyerInformation";
import BuyerDashboard from "./pages/BuyerDashboard";
import BuyerDealDetail from "./pages/BuyerDealDetail";
import BuyerWatchlist from "./pages/BuyerWatchlist";
import BuyerNDA from "./pages/BuyerNDA";
import AdvisorDashboard from "./pages/AdvisorDashboard";
import AdvisorCalendar from "./pages/AdvisorCalendar";
import AdvisorDealRoom from "./pages/AdvisorDealRoom";
import AdvisorSellerDetail from "./pages/AdvisorSellerDetail";
import AdvisorDocuments from "./pages/AdvisorDocuments";
import AdvisorPartyDetail from "./pages/AdvisorPartyDetail";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ARAGON_COLORS, aragonTheme } from "./theme/AragonTheme";

function PortalPlaceholder({ role }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const signOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        bgcolor: ARAGON_COLORS.navy,
        color: ARAGON_COLORS.ivory,
        px: 3,
      }}
    >
      <Box sx={{ maxWidth: 560, textAlign: "center" }}>
        <Typography sx={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>
          Protected Aragon world
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontSize: { xs: 38, md: 50 },
            lineHeight: 1,
          }}
        >
          {role} portal
        </Typography>
        <Typography sx={{ mt: 2, color: ARAGON_COLORS.ivory, opacity: 0.72, lineHeight: 1.65 }}>
          Authentication and role protection are active. This portal will be rebuilt in the next product world.
        </Typography>
        <Button
          onClick={signOut}
          sx={{
            mt: 3,
            bgcolor: ARAGON_COLORS.ivory,
            color: ARAGON_COLORS.navy,
            px: 3,
            "&:hover": { bgcolor: ARAGON_COLORS.steel, color: ARAGON_COLORS.ivory },
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/auth/:mode" element={<Authenticate />} />
      <Route path="/contact" element={<PublicInfo />} />
      <Route path="/privacy" element={<PublicInfo />} />
      <Route path="/terms" element={<PublicInfo />} />
      <Route path="/process" element={<Intake />} />
      <Route path="/seller/dashboard" element={<SellerDashboard />} />
      <Route path="/seller/valuation" element={<ValuationResult />} />
      <Route path="/seller/deal-room" element={<DealRoom />} />
      <Route path="/seller/documents" element={<SellerDocument />} />
      <Route path="/seller/document" element={<Navigate to="/seller/documents" replace />} />
      <Route element={<ProtectedRoute roles={["seller"]} />}>
        <Route path="/seller/*" element={<PortalPlaceholder role="Seller" />} />
      </Route>
      <Route path="/buyer/information" element={<BuyerInformation />} />
      <Route path="/buyer" element={<Navigate to="/buyer/dashboard" replace />} />
      <Route path="/buyer/dashboard" element={<BuyerDashboard />} />
      <Route path="/buyer/deals/:dealId" element={<BuyerDealDetail />} />
      <Route path="/buyer/watchlist" element={<BuyerWatchlist />} />
      <Route path="/buyer/nda" element={<BuyerNDA />} />
      <Route element={<ProtectedRoute roles={["buyer"]} />}>
        <Route path="/buyer/*" element={<PortalPlaceholder role="Buyer" />} />
      </Route>
      <Route path="/advisor" element={<Navigate to="/advisor/dashboard" replace />} />
      <Route path="/advisor/dashboard" element={<AdvisorDashboard />} />
      <Route path="/advisor/calendar" element={<AdvisorCalendar />} />
      <Route path="/advisor/deal-room" element={<AdvisorDealRoom />} />
      <Route path="/advisor/deal-room/:sellerId" element={<AdvisorSellerDetail />} />
      <Route path="/advisor/documents" element={<AdvisorDocuments />} />
      <Route path="/advisor/parties/:partyType/:partyId" element={<AdvisorPartyDetail />} />
      <Route element={<ProtectedRoute roles={["advisor"]} />}>
        <Route path="/advisor/*" element={<PortalPlaceholder role="Advisor" />} />
      </Route>
      <Route element={<ProtectedRoute roles={["partner"]} />}>
        <Route path="/partner/*" element={<PortalPlaceholder role="Partner" />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={aragonTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
